import { IconArrowRight, IconExternalLink } from "@tabler/icons-react"
import { type FC } from "react"

import Button from "~/components/Button"
import { useAlert } from "~/contexts/AlertContext"
import { query } from "~/utilities/trpc"

type Props = { ucraftSiteUrl: string; newSiteUrl: string; onFinished(): Promise<void> }
const UcraftMigrationBanner: FC<Props> = ({ ucraftSiteUrl, newSiteUrl, onFinished }) => {
	const alert = useAlert()

	const finishMigration = query.landing.finishUcraftMigration.useMutation({
		onSuccess: onFinished,
		async onError(error) {
			console.error(error)
			await alert.show("Error", "Failed to finish migration. Please try again.")
		},
	})

	const discardMigration = query.landing.discardUcraftMigration.useMutation({
		onSuccess: onFinished,
		async onError(error) {
			console.error(error)
			await alert.show("Error", "Failed to discard migration. Please try again.")
		},
	})

	const handleDiscard = async () => {
		const { result, close } = await alert.confirm(
			"Discard migrated site?",
			"This will delete the new landing page and take you back to your Ucraft site. You can migrate again at any time.",
			{ confirmLabel: "Discard site" },
		)
		if (result) {
			discardMigration.mutateAsync().finally(() => close())
		}
	}

	const isBusy = finishMigration.isPending || discardMigration.isPending

	return (
		<div className="border-b border-amber-200 bg-amber-50 px-6 py-4">
			<div className="mx-auto flex w-full max-w-7xl flex-wrap items-center justify-between gap-4">
				<div className="flex min-w-0 flex-col gap-1">
					<p className="text-sm font-semibold text-amber-900">Migration preview - both sites are live</p>
					<div className="flex flex-wrap items-center gap-2 text-xs text-amber-800">
						<a
							href={ucraftSiteUrl}
							target="_blank"
							rel="noreferrer noopener"
							className="inline-flex items-center gap-1 font-medium underline"
						>
							Ucraft site
							<IconExternalLink className="size-3" />
						</a>
						<IconArrowRight className="size-3" />
						<a
							href={newSiteUrl}
							target="_blank"
							rel="noreferrer noopener"
							className="inline-flex items-center gap-1 font-medium underline"
						>
							New landing page
							<IconExternalLink className="size-3" />
						</a>
					</div>
				</div>
				<div className="flex items-center gap-2">
					<Button
						variant="secondary-danger"
						onClick={handleDiscard}
						isLoading={discardMigration.isPending}
						isDisabled={isBusy}
					>
						Discard
					</Button>
					<Button
						variant="primary"
						onClick={() => finishMigration.mutate()}
						isLoading={finishMigration.isPending}
						isDisabled={isBusy}
					>
						Finish migration
					</Button>
				</div>
			</div>
		</div>
	)
}

export default UcraftMigrationBanner
